import { ArrowRight, Download } from "lucide-react";

export const Hero = () => {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative overflow-hidden pt-[78px] pb-[54px] sm:pt-[86px] sm:pb-[62px]"
    >
      {/* Animated grid background */}
      <div className="absolute inset-0 grid-bg" aria-hidden />
      {/* Electric blue glow */}
      <div
        aria-hidden
        className="hero-spotlight absolute left-1/2 top-[38%] w-[900px] h-[900px] rounded-full blur-3xl"
        style={{
          background:
            "radial-gradient(circle at center, rgba(0,102,255,0.18) 0%, rgba(0,102,255,0.06) 35%, transparent 70%)",
        }}
      />
      {/* Scan line */}
      <div
        aria-hidden
        className="absolute inset-0 overflow-hidden pointer-events-none"
      >
        <div className="scan-line absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#0066FF]/40 to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-7">
          <h1
            data-testid="hero-heading"
            className="font-display reveal text-5xl sm:text-6xl lg:text-[5rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-zinc-950 text-balance"
          >
            Ditch the noise.{" "}
            <span className="relative inline-block">
              Stream
              <span
                aria-hidden
                className="absolute left-0 right-0 -bottom-1 h-[10px] bg-[#0066FF]/15 -z-10"
              />
            </span>{" "}
            the <span className="italic font-medium text-zinc-700">execution</span>.
          </h1>

          <p
            data-testid="hero-subheading"
            className="reveal reveal-delay-2 mt-7 max-w-3xl text-lg sm:text-xl text-zinc-600 font-light leading-relaxed text-pretty"
          >
            Meet <span className="text-zinc-950 font-medium">Vibecast</span>, a
            focused streaming platform built for professionals who use video to
            learn, build, and stay ahead.
            <br />
            <br />
            Designed around engineering, UI/UX, product, architecture, and
            other professional knowledge, Vibecast removes the
            entertainment-first mechanics of mainstream video platforms and
            creates an environment built for focused learning.
          </p>

          <div className="reveal reveal-delay-3 mt-10 flex flex-wrap items-center gap-3">
            <a
              href="#contact"
              data-testid="hero-cta-launch"
              className="group inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#0052CC] text-white text-sm font-medium pl-5 pr-4 h-12 rounded-full transition-all duration-300 shadow-[0_8px_24px_-8px_rgba(0,102,255,0.55)]"
            >
              Explore Vibecast
              <span className="grid place-items-center w-7 h-7 rounded-full bg-white/15 group-hover:bg-white/25 transition">
                <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </a>
            <a
              href="#vibecast"
              data-testid="hero-cta-download"
              className="group inline-flex items-center gap-2 bg-white border border-black/15 hover:border-black/40 text-zinc-950 text-sm font-medium pl-5 pr-5 h-12 rounded-full transition-all duration-300"
            >
              <Download className="w-4 h-4" />
              Download Mobile App
              <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500 border border-black/10 rounded-full px-2 py-0.5">
                Beta soon
              </span>
            </a>
          </div>

          {/* stat row */}
          <div className="reveal reveal-delay-4 mt-20 grid grid-cols-2 sm:grid-cols-4 gap-y-6 gap-x-8 border-t border-black/5 pt-10 max-w-3xl">
            {[
              { k: "Format", v: "Deep-focus streams" },
              { k: "Encoding", v: "Multi-bitrate HLS" },
              { k: "Distractions", v: "Zero algorithm" },
              { k: "Audience", v: "Tech professionals" },
            ].map((s) => (
              <div key={s.k} data-testid={`hero-stat-${s.k.toLowerCase()}`}>
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">
                  {s.k}
                </div>
                <div className="mt-1.5 font-display text-[17px] font-semibold text-zinc-950 tracking-tight">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
          </div>

          {/* 3D mobile mockup — right column */}
          <div className="lg:col-span-5 reveal reveal-delay-2 relative flex justify-center lg:justify-end items-start -mt-6 -mb-6 lg:-mt-24 lg:-mb-16">
            <div className="relative w-full max-w-[285px] lg:max-w-[75%] lg:ml-auto">
              {/* Ambient blue glow behind phone */}
              <div
                aria-hidden
                className="absolute inset-0 -z-10 blur-3xl opacity-70"
                style={{
                  background:
                    "radial-gradient(closest-side, rgba(0,102,255,0.35), rgba(0,102,255,0.08) 60%, transparent 80%)",
                }}
              />
              {/* Soft ground shadow */}
              <div
                aria-hidden
                className="absolute left-1/2 -translate-x-1/2 bottom-2 h-6 w-3/4 rounded-full blur-2xl bg-zinc-900/30"
              />
              <img
                src="/vibecast-mobile.png"
                alt="Vibecast mobile app — tilted 3D mockup showing a dark-mode video tutorial player with a live code editor and presenter panel"
                data-testid="hero-mobile-mockup"
                loading="eager"
                className="relative w-full h-auto select-none pointer-events-none drop-shadow-[0_40px_60px_rgba(15,23,42,0.25)]"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
